import { computed, Signal, signal } from '@angular/core';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';

interface TableStyleConfig {
  tableClassName: string;
  striped?: boolean;
  gridlines?: boolean;
}

interface TableConfigLike {
  style: TableStyleConfig;
}

/**
 * Base reutilizable para dialogos de busqueda con paginacion por cursor.
 */
export abstract class BaseBusquedaCursorDialog<TItem> {
  items = signal<TItem[]>([]);
  loading = signal(false);

  totalRegistros = signal(0);
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly totalPages = computed(() => {
    const total = this.totalRegistros();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  // Flags de navegacion: los fija la RESPUESTA del backend (frontend-girop 17.7).
  hasPrevious = signal(false);
  hasNext = signal(false);

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    const fin = this.registroInicio() + this.items().length - 1;
    return Math.min(fin, this.totalRegistros());
  });

  protected resetPaginacionCursor(): void {
    this.items.set([]);
    this.totalRegistros.set(0);
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.pageSize.set(DEFAULT_ROWS_PER_PAGE);
    this.selectedPageSize = DEFAULT_ROWS_PER_PAGE;
  }

  onSelectedPageSizeChange(size: number): void {
    this.selectedPageSize = size;
  }

  protected buildTableStyleClass(
    tableConfigSignal: Signal<TableConfigLike>
  ): Signal<string> {
    return computed(() => {
      const config = tableConfigSignal();
      const classes = ['p-datatable-sm', config.style.tableClassName];
      if (config.style.gridlines !== false) {
        classes.push('p-datatable-gridlines');
      }
      if (config.style.striped !== false) {
        classes.push('p-datatable-striped');
      }
      return classes.join(' ');
    });
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
